import { useEffect, useState } from 'react';

import { axiosInstance } from '../utils/axiosInstance';
import { triggerEvent } from '../utils';

import {
  isOrderCpfTestCategory,
  shouldAskCpf as resolveShouldAskCpf,
} from '../modules/requestForm/cpfField';

import { type IUserApiResponse } from '../pages/api/users';

interface IUseOrderCpfTestParams {
  slug?: string | null;
  categoryId?: number;
}

interface IUseOrderCpfTestResult {
  isResolved: boolean;
  flagEnabled: boolean;
  hasCpf: boolean;
  shouldAskCpf: boolean;
}

interface IState {
  isResolved: boolean;
  flagEnabled: boolean;
  hasCpf: boolean;
}

const API = process.env.NEXT_PUBLIC_API_CLIENTS;

async function fetchFlag(): Promise<boolean> {
  try {
    const { data } = await axiosInstance<boolean>({
      url: `${API}/feature/order-cpf-test`,
      method: 'GET',
    });

    return data === true;
  } catch {
    return false;
  }
}

async function fetchHasCpf(): Promise<boolean> {
  try {
    const { data } = await axiosInstance<IUserApiResponse>({
      url: `${API}/users`,
      method: 'GET',
    });

    return Boolean(data?.data?.hasCpf);
  } catch {
    return false;
  }
}

/**
 * ECA Digital (RAD-44): decides whether the order form must ask for the CPF.
 *
 * Only categories under test trigger any request. For them, the hook resolves the
 * `order-cpf-test` feature flag and whether the logged user already has a CPF,
 * and reports the outcome through `json_form-cpf_shown` / `json_form-cpf_skipped`.
 */
export function useOrderCpfTest({
  slug,
  categoryId,
}: IUseOrderCpfTestParams): IUseOrderCpfTestResult {
  const isTestCategory = isOrderCpfTestCategory(slug);

  const [state, setState] = useState<IState>({
    isResolved: !isTestCategory,
    flagEnabled: false,
    hasCpf: false,
  });

  useEffect(() => {
    if (!isTestCategory) {
      setState({ isResolved: true, flagEnabled: false, hasCpf: false });
      return undefined;
    }

    let isActive = true;

    async function resolve() {
      const [flagEnabled, hasCpf] = await Promise.all([
        fetchFlag(),
        fetchHasCpf(),
      ]);

      if (isActive) {
        setState({ isResolved: true, flagEnabled, hasCpf });
      }
    }

    resolve();

    return () => {
      isActive = false;
    };
  }, [isTestCategory]);

  const shouldAskCpf = resolveShouldAskCpf({
    flagEnabled: state.flagEnabled,
    slug,
    hasCpf: state.hasCpf,
  });

  useEffect(() => {
    if (!state.isResolved || !isTestCategory || !state.flagEnabled) {
      return;
    }

    if (!categoryId) {
      return;
    }

    if (shouldAskCpf) {
      triggerEvent('json_form-cpf_shown', { categoryID: categoryId });
      return;
    }

    if (state.hasCpf) {
      triggerEvent('json_form-cpf_skipped', {
        categoryID: categoryId,
        reason: 'has_cpf',
      });
    }
  }, [
    state.isResolved,
    state.flagEnabled,
    state.hasCpf,
    isTestCategory,
    shouldAskCpf,
    categoryId,
  ]);

  return {
    isResolved: state.isResolved,
    flagEnabled: state.flagEnabled,
    hasCpf: state.hasCpf,
    shouldAskCpf,
  };
}
